import Flickity from 'flickity';

import QuickViewPopup from '../features/quick-view-popup';
import {register} from '../vendor/theme-scripts/theme-sections';
import {makeGridSwatches} from '../features/swatch';

const sections = {};

const selectors = {
  slider: '[data-slider]',
  sliderItem: '[data-slide-item]',
  pointer: '[data-pointer]',
  productGridItemImage: '[data-product-media-container]',
  links: 'a, button',
  flickityButton: '.flickity-button',
};

const attributes = {
  pointer: 'data-pointer',
  tabIndex: 'tabindex',
};

const classes = {
  pointerSelected: 'pointer--selected',
  pointerInner: 'pointer__inner',
  isSelected: 'is-selected',
};

class Look {
  constructor(section) {
    this.container = section.container;
    this.slider = this.container.querySelector(selectors.slider);
    this.slides = this.container.querySelectorAll(selectors.sliderItem);
    this.pointers = this.container.querySelectorAll(selectors.pointer);
    this.flkty = null;

    this.checkSlidesSizeOnResize = () => this.checkSlidesSize();
    this.onDrawerOpen = () => this.drawerOpen();
    this.pointersInit = (evt) => this.dotPointers(evt);
    this.pointersOver = () => this.dotPointerIn();
    this.pointersOut = () => this.dotPointerOut();

    new QuickViewPopup(this.container);
    this.listen();
    makeGridSwatches(this.container);
  }

  listen() {
    if (this.slider) {
      this.checkSlidesSize();
      document.addEventListener('theme:resize:width', this.checkSlidesSizeOnResize);
      document.addEventListener('theme:drawer:open', this.onDrawerOpen);
    }

    if (this.pointers.length > 0) {
      this.pointers.forEach((pointer) => {
        pointer.addEventListener('click', this.pointersInit);
        pointer.addEventListener('mouseover', this.pointersOver);
        pointer.addEventListener('mouseleave', this.pointersOut);
      });
    }
  }

  checkSlidesSize() {
    const isDesktop = window.innerWidth >= theme.sizes.small;

    if (isDesktop) {
      PaloAlto.Drawer.close();

      if (this.slides.length > 2) {
        this.initSlider();
      } else {
        this.destroySlider();
        this.slidesTabIndex();
      }

      return;
    }

    if (!isDesktop && this.slides.length > 1) {
      this.initSlider();

      return;
    }

    this.destroySlider();
  }

  initSlider() {
    if (this.flkty === null) {
      this.flkty = new Flickity(this.slider, {
        prevNextButtons: true,
        wrapAround: true,
        adaptiveHeight: false,
        cellAlign: 'left',
        groupCells: false,
        contain: true,
        on: {
          ready: () => {
            this.slidesTabIndex();
            this.setSliderArrowsPosition();
            this.dotPointers();
          },
          change: () => {
            this.slidesTabIndex();
            this.dotPointers();
          },
        },
      });

      return;
    }

    this.setSliderArrowsPosition();
  }

  drawerOpen() {
    if (this.flkty !== null) {
      setTimeout(() => {
        this.flkty.resize();
      }, 0);
    }
  }

  setSliderArrowsPosition() {
    const isDesktop = window.innerWidth >= theme.sizes.small;

    if (isDesktop) {
      const arrows = this.slider.querySelectorAll(selectors.flickityButton);
      const image = this.slider.querySelector(selectors.productGridItemImage);

      if (arrows.length && image) {
        arrows.forEach((arrow) => {
          arrow.style.top = `${image.offsetHeight / 2}px`;
        });
      }
    }
  }

  slidesTabIndex() {
    if (this.slides.length < 3) {
      this.slider.querySelectorAll(selectors.links).forEach((link) => {
        link.setAttribute(attributes.tabIndex, '0');
      });

      return;
    }

    const slider = Flickity.data(this.slider);

    slider.cells.forEach((slide) => {
      slide.element.querySelectorAll(selectors.links).forEach((link) => {
        link.setAttribute(attributes.tabIndex, '-1');
      });
    });

    slider.cells.forEach((slide) => {
      if (slide.element.classList.contains(classes.isSelected)) {
        slide.element.querySelectorAll(selectors.links).forEach((link) => {
          link.setAttribute(attributes.tabIndex, '0');
        });

        const secondaryElement = slide.element.nextSibling ? slide.element.nextSibling : slide.element.parentNode.firstChild; // Used to add tabindex = 0 to the other slide element that's visible as well, but is not marked as selected

        secondaryElement.querySelectorAll(selectors.links).forEach((link) => {
          link.setAttribute(attributes.tabIndex, '0');
        });
      }
    });
  }

  destroySlider() {
    if (typeof this.flkty === 'object' && this.flkty !== null) {
      this.flkty.destroy();
      this.flkty = null;
    }
  }

  dotPointers(evt) {
    if (this.pointers.length === 0) {
      return;
    }

    this.pointers.forEach((button) => {
      button.classList.remove(classes.pointerSelected);
    });

    if (evt) {
      const dotIndex = evt.target.getAttribute(attributes.pointer);
      this.flkty.select(dotIndex);

      return;
    }

    const slideIndex = this.flkty == null ? 0 : this.flkty.selectedIndex;

    if (slideIndex >= 0) {
      this.pointers[slideIndex].classList.add(classes.pointerSelected);
    }
  }

  dotPointerIn() {
    this.pointers.forEach((pointer) => {
      pointer.style.setProperty('--look-animation', 'none');
    });
  }

  dotPointerOut() {
    this.pointers.forEach((pointer) => {
      pointer.style.removeProperty('--look-animation');
    });
  }

  /**
   * Event callback for Theme Editor `section:block:select` event
   */
  onBlockSelect(evt) {
    const flkty = Flickity.data(this.slider) || null;
    const index = parseInt([...evt.target.parentNode.children].indexOf(evt.target));

    if (flkty !== null) {
      flkty.select(index);
    }
  }

  /**
   * Event callback for Theme Editor `section:unload` event
   */
  onUnload() {
    document.removeEventListener('theme:resize:width', this.checkSlidesSizeOnResize);
    document.removeEventListener('theme:drawer:open', this.onDrawerOpen);
  }
}

const lookSection = {
  onLoad() {
    sections[this.id] = new Look(this);
  },
  onUnload(e) {
    sections[this.id].onUnload(e);
  },
  onBlockSelect(e) {
    sections[this.id].onBlockSelect(e);
  },
};

register('look', lookSection);
